import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiBookOpen, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import Button from '../common/Button';

export default function CurrentTeaching({ catechism, loading }) {
  const [activeIndex, setActiveIndex] = useState(0);

  // Handle both array format (new) and single object format (backwards compatible)
  const catechisms = Array.isArray(catechism)
    ? catechism
    : catechism?.questions
      ? catechism.questions
      : catechism
        ? [catechism]
        : [];

  // Default content if no catechism data
  const defaultContent = [{
    questionNumber: 27,
    question: 'What misery did the fall bring upon mankind?',
    answer: 'The fall brought upon mankind the loss of communion with God, his displeasure and curse; so as we are by nature children of wrath, bond slaves to Satan, and justly liable to all punishments in this world, and that which is to come.',
    catechismName: 'Westminster Larger Catechism',
    catechismAbbreviation: 'WLC',
    catechismType: 'westminster_larger',
  }];

  const questions = catechisms.length > 0 ? catechisms : defaultContent;
  const currentQuestion = questions[activeIndex];
  const hasMultiple = questions.length > 1;

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? questions.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === questions.length - 1 ? 0 : prev + 1));
  };

  // Get display name and abbreviation
  const getCatechismDisplay = (q) => {
    if (q.catechismName) {
      // Split "Westminster Larger Catechism" into ["Westminster", "Larger Catechism"]
      const parts = q.catechismName.split(' ');
      if (parts.length >= 2) {
        return {
          prefix: parts[0],
          name: parts.slice(1).join(' '),
        };
      }
      return { prefix: '', name: q.catechismName };
    }
    return { prefix: 'Westminster', name: 'Catechism' };
  };

  const display = getCatechismDisplay(currentQuestion);

  return (
    <section className="bg-primary text-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Visual/Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="aspect-square max-w-md mx-auto lg:mx-0 bg-primary-700 rounded-lg flex items-center justify-center relative overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-full h-full">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute border border-white/20 rounded-full"
                      style={{
                        width: `${(i + 1) * 20}%`,
                        height: `${(i + 1) * 20}%`,
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Series/Category Label */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="relative z-10 text-center px-8"
                >
                  <FiBookOpen className="w-16 h-16 mx-auto mb-4 text-accent/80" />
                  <p className="text-accent text-xl font-medium mb-2">{display.prefix}</p>
                  <p className="text-3xl md:text-4xl font-serif font-light">
                    {display.name}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation dots for multiple catechisms */}
            {hasMultiple && (
              <div className="flex justify-center mt-6 gap-2">
                {questions.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      idx === activeIndex
                        ? 'bg-accent w-6'
                        : 'bg-white/30 hover:bg-white/50'
                    }`}
                    aria-label={`Go to catechism ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            {/* Header with navigation arrows */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-3xl md:text-4xl font-light">
                Weekly Catechism
              </h2>

              {hasMultiple && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePrev}
                    className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                    aria-label="Previous catechism"
                  >
                    <FiChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-sm text-white/60 min-w-[3rem] text-center">
                    {activeIndex + 1} / {questions.length}
                  </span>
                  <button
                    onClick={handleNext}
                    className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                    aria-label="Next catechism"
                  >
                    <FiChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {currentQuestion && (
                  <>
                    <p className="text-accent text-sm font-medium mb-3">
                      Question #{currentQuestion.questionNumber || currentQuestion.question_number}
                    </p>

                    <h3 className="text-xl md:text-2xl font-medium mb-4 leading-relaxed">
                      {currentQuestion.question}
                    </h3>

                    <p className="text-white/70 text-lg leading-relaxed mb-8 line-clamp-4">
                      {currentQuestion.answer}
                    </p>
                  </>
                )}
              </motion.div>
            </AnimatePresence>

            <Button to="/catechisms" variant="light">
              {hasMultiple ? 'Explore All Catechisms' : 'Read Weekly Catechism'}
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
